import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'
import FAQSection from '../components/FAQSection'

export default function DestinationDetail() {
  const { slug } = useParams()
  const [destination, setDestination] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    const fetchDestination = async () => {
      try {
        setLoading(true)

        // Try to find destination by matching slug (case-insensitive, handle spaces/hyphens)
        // First, convert URL slug back to potential database format
        const possibleSlugs = [
          slug, // Original: "sesse-island"
          slug.replace(/-/g, ' '), // With spaces: "sesse island"
          slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' '), // Title case: "Sesse island"
          slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '), // Proper title: "Sesse Island"
        ]

        let foundDestination = null

        // Try fetching all destinations and find the match
        const response = await fetch('http://localhost:3001/api/destinations?limit=100')

        if (!response.ok) {
          throw new Error('Failed to fetch destinations')
        }

        const data = await response.json()

        // Find destination by matching slug (case-insensitive)
        foundDestination = data.docs.find(dest => {
          const destSlug = (dest.slug || '').toLowerCase().replace(/\s+/g, '-')
          const urlSlug = slug.toLowerCase().replace(/\s+/g, '-')
          return destSlug === urlSlug && dest.status === 'published'
        })

        if (foundDestination) {
          setDestination(foundDestination)
        } else {
          throw new Error('Destination not found')
        }

        setError(null)
      } catch (err) {
        console.error('Error fetching destination:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchDestination()
  }, [slug])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-safari-green"></div>
          <p className="text-gray-600 mt-4">Loading destination...</p>
        </div>
      </div>
    )
  }

  if (error || !destination) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-2xl mx-auto px-4">
          <div className="text-6xl mb-4">🗺️</div>
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Destination Not Found</h1>
          <p className="text-gray-600 mb-8">
            Sorry, we couldn't find the destination you're looking for.
          </p>
          <Link
            to="/destinations"
            className="btn-primary inline-block"
          >
            View All Destinations
          </Link>
        </div>
      </div>
    )
  }

  const heroImage = destination.heroImage?.sizes?.hero?.url || destination.heroImage?.url || destination.featuredImage?.sizes?.hero?.url || destination.featuredImage?.url

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[500px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroImage})`,
            filter: 'brightness(0.6)'
          }}
        />
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 font-heading drop-shadow-lg">
            {destination.name}
          </h1>
          <p className="text-xl md:text-2xl drop-shadow-lg">
            {destination.excerpt}
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-safari-light p-6 rounded-xl text-center border border-safari-green">
                <FontAwesomeIcon icon="fa-map-marker-alt" className="w-8 h-8 text-safari-green mx-auto mb-3" />
                <h3 className="font-bold text-safari-green mb-2">Region</h3>
                <p className="text-gray-700">{destination.region?.charAt(0).toUpperCase() + destination.region?.slice(1) || 'Uganda'}</p>
              </div>
              <div className="bg-safari-light p-6 rounded-xl text-center border border-safari-green">
                <FontAwesomeIcon icon="fa-tag" className="w-8 h-8 text-safari-green mx-auto mb-3" />
                <h3 className="font-bold text-safari-green mb-2">Category</h3>
                <p className="text-gray-700">{destination.category?.charAt(0).toUpperCase() + destination.category?.slice(1) || 'Safari'}</p>
              </div>
              <div className="bg-safari-light p-6 rounded-xl text-center border border-safari-green">
                <FontAwesomeIcon icon="fa-star" className="w-8 h-8 text-safari-green mx-auto mb-3" />
                <h3 className="font-bold text-safari-green mb-2">Status</h3>
                <p className="text-gray-700">Open Year-Round</p>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-safari-green mb-6">About {destination.name}</h2>
              {destination.description && destination.description.map((block, index) => (
                <div key={index} className="mb-4">
                  {block.type === 'h1' && (
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                      {block.children.map((child, i) => (
                        <span key={i} className={child.bold ? 'font-bold' : ''}>
                          {child.text}
                        </span>
                      ))}
                    </h1>
                  )}
                  {block.type === 'h2' && (
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">
                      {block.children.map((child, i) => (
                        <span key={i} className={child.bold ? 'font-bold' : ''}>
                          {child.text}
                        </span>
                      ))}
                    </h2>
                  )}
                  {block.type === 'h3' && (
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {block.children.map((child, i) => (
                        <span key={i} className={child.bold ? 'font-bold' : ''}>
                          {child.text}
                        </span>
                      ))}
                    </h3>
                  )}
                  {block.type === 'h4' && (
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      {block.children.map((child, i) => (
                        <span key={i} className={child.bold ? 'font-bold' : ''}>
                          {child.text}
                        </span>
                      ))}
                    </h4>
                  )}
                  {(!block.type || block.type === 'p') && (
                    <p className="text-gray-700 leading-relaxed mb-4">
                      {block.children.map((child, i) => (
                        <span key={i} className={child.bold ? 'font-bold' : child.italic ? 'italic' : ''}>
                          {child.text}
                        </span>
                      ))}
                    </p>
                  )}
                  {block.type === 'ul' && (
                    <ul className="list-disc list-inside mb-4">
                      {block.children.map((item, i) => (
                        <li key={i} className="text-gray-700 mb-2">
                          {item.children ? item.children.map((child, j) => (
                            <span key={j} className={child.bold ? 'font-bold' : ''}>
                              {child.text}
                            </span>
                          )) : item.text}
                        </li>
                      ))}
                    </ul>
                  )}
                  {block.type === 'ol' && (
                    <ol className="list-decimal list-inside mb-4">
                      {block.children.map((item, i) => (
                        <li key={i} className="text-gray-700 mb-2">
                          {item.children ? item.children.map((child, j) => (
                            <span key={j} className={child.bold ? 'font-bold' : ''}>
                              {child.text}
                            </span>
                          )) : item.text}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      {destination.highlights && destination.highlights.length > 0 && (
        <section className="py-16 bg-safari-light">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-safari-green font-heading">
                Highlights & Activities
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {destination.highlights.map((highlight, index) => (
                  <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <div className="flex items-start gap-4">
                      <div className="bg-safari-green text-white p-3 rounded-lg">
                        <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                        <p className="text-gray-700 leading-relaxed">{highlight.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {destination.gallery && destination.gallery.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-safari-green font-heading">
                Photo Gallery
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {destination.gallery.map((image, index) => (
                  <div key={index} className="relative h-64 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <img
                      src={image.sizes?.card?.url || image.url}
                      alt={image.alt || `${destination.name} gallery ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {destination.faqs && destination.faqs.length > 0 && (
        <FAQSection faqs={destination.faqs} />
      )}

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-safari-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center" style={{ border: '1px solid #24684f' }}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-safari-green font-heading">
              Ready to Explore {destination.name}?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-700">
              Book your adventure today and experience the beauty of Uganda with expert guides and sustainable tourism practices.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-safari-gold text-white px-8 py-4 rounded-lg font-bold hover:bg-safari-green transition-all duration-300 shadow-lg text-lg w-full sm:w-auto"
              >
                Book This Experience
              </button>
              <Link
                to="/destinations"
                className="border-2 border-safari-green text-safari-green px-8 py-4 rounded-lg font-bold hover:bg-safari-green hover:text-white transition-all duration-300 shadow-lg text-lg w-full sm:w-auto"
              >
                View All Destinations
              </Link>
            </div>
          </div>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={destination.name}
      />
    </div>
  )
}
